import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { SetCandidatePlanning } from '@stores/candidate/candidate.actions';
import { OpsEventWebservice } from '@webservices/ops-event-api/ops-event.webservice';
import { EMPTY, Subject } from 'rxjs';
import { catchError, exhaustMap } from 'rxjs/operators';

/**
 * Recharge le planning du candidat depuis l'API (`GET /planning`) et le pousse dans le store.
 *
 * Le statut des entretiens n'est pas poussé en temps réel (un start/end côté ops-event n'est qu'un
 * write Mongo, sans publication Mercure) : ce re-fetch est le seul moyen de récupérer le statut
 * serveur frais.
 *
 * Les déclenchements passent par un `Subject` consommé en `exhaustMap` : tant qu'un `GET /planning`
 * est en cours, les appels concurrents sont ignorés (ex. `window:focus` + `document:visibilitychange`
 * émis ensemble au retour au premier plan, sur mobile comme desktop).
 */
@Injectable({ providedIn: 'root' })
export class PlanningRefreshService {
  private readonly opsEventWebservice = inject(OpsEventWebservice);
  private readonly store = inject(Store);

  private readonly refreshTrigger$ = new Subject<void>();

  constructor() {
    this.refreshTrigger$
      .pipe(
        exhaustMap(() =>
          this.opsEventWebservice.getCandidatePlaning().pipe(
            // 428 (planning pas encore généré) / erreur réseau : on ne touche pas au store et le flux
            // reste vivant pour les prochains déclenchements (catchError par requête, à l'intérieur).
            catchError(() => EMPTY)
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((planning) => this.store.dispatch(new SetCandidatePlanning(planning)));
  }

  refresh(): void {
    this.refreshTrigger$.next();
  }
}
